/** @jsxImportSource @emotion/react */
import {
  EuiButton,
  EuiButtonEmpty,
  EuiForm,
  EuiModal,
  EuiModalBody,
  EuiModalFooter,
  EuiModalHeader,
  EuiModalHeaderTitle,
  htmlIdGenerator,
} from "@inscopix/ideas-eui";
import { DataTableColumnDefinition } from "pages/gdt/store/DataTableProvider.types";
import { PanelCreateColumnBoolean } from "./panels/PanelCreateColumnBoolean";
import { PanelCreateColumnFile } from "./panels/PanelCreateColumnFile";
import { PanelCreateColumnText } from "./panels/PanelCreateColumnText";
import { PanelCreateColumnFloat } from "./panels/PanelCreateColumnFloat";
import { PanelCreateColumnInteger } from "./panels/PanelCreateColumnInteger";
import { PanelCreateColumnFileMetadatum } from "./panels/PanelCreateColumnFileMetadatum";
import { useCallback, useState } from "react";
import { FieldColumnType } from "../fields/FieldColumnType";
import { css } from "@emotion/react";
import { addUtilityToastFailure } from "utils/addUtilityToastFailure";
import { addUtilityToastSuccess } from "utils/addUtilityToastSuccess";
import { useDataTableContext } from "pages/gdt/store/DataTableProvider";
import assert from "assert";
import { PanelCreateColumnChoice } from "./panels/PanelCreateColumnChoice";
import { ModalCreateColumnFromExistingTable } from "./ModalCreateColumnFromExistingTable";
import { ModalCreateAnalysisTable } from "./ModalCreateAnalysisTable";
import { PanelCreateColumnDate } from "./panels/PanelCreateColumnDate";

export const COLUMN_KIND_MAP = {
  file: {
    name: "File",
    icon: "tokenFile",
    editable: true,
  },
  metadatum: {
    name: "File Metadatum",
    icon: "tokenObject",
    editable: false,
  },
  text: {
    name: "Text",
    icon: "tokenText",
    editable: true,
  },
  choice: {
    name: "Choice",
    icon: "tokenFlattened",
    editable: true,
  },
  boolean: {
    name: "Boolean",
    icon: "tokenBoolean",
    editable: true,
  },
  float: {
    name: "Float",
    icon: "tokenNumber",
    editable: true,
  },
  integer: {
    name: "Integer",
    icon: "tokenNumber",
    editable: true,
  },
  date: {
    name: "Date",
    icon: "tokenDate",
    editable: true,
  },
  identifier: {
    name: "Identifier",
    icon: "tokenAnnotation",
    editable: true,
  },
  join: {
    name: "Join",
    icon: "tokenJoin",
    editable: false,
  },
  roi_frame: {
    name: "ROI Frame",
    icon: "tokenShape",
    editable: true,
  },
  cell_status: {
    name: "Cell Status",
    icon: "heatmap",
    editable: true,
  },
  resource_tier: {
    name: "Resource Tier",
    icon: "compute",
    editable: false,
  },
} as const satisfies Record<
  DataTableColumnDefinition["kind"],
  { name: string; icon: string; editable: boolean }
>;

export type ColumnType =
  | DataTableColumnDefinition["kind"]
  | "join_existing_table"
  | "new_analysis_table";

type CreatableDataTableColumnType = Exclude<ColumnType, "resource_tier">;

export type ModalCreateColumnFields = {
  name: {
    value: string;
    isValid: boolean;
    isDirty: boolean;
  };
  default_formula: {
    value: string | undefined;
    isValid: boolean;
  };
  definition: {
    value: DataTableColumnDefinition | undefined;
    isValid: boolean;
  };
};
const INITIAL_FIELDS: ModalCreateColumnFields = {
  name: {
    value: "",
    isValid: false,
    isDirty: false,
  },
  default_formula: {
    value: undefined,
    isValid: true,
  },
  definition: {
    value: undefined,
    isValid: false,
  },
};

export type PanelProps = {
  autoFocus?: boolean;
  isDefinitionEditable?: boolean;
  fields: ModalCreateColumnFields;
  onFieldChange: <K extends keyof ModalCreateColumnFields>(
    fieldName: K,
    field: ModalCreateColumnFields[K],
  ) => void;
};

interface ModalCreateColumnProps {
  onClose: () => void;
  tableId: string;
}

/** Component that renders a modal for creating a new column */
export const ModalCreateColumn = ({
  onClose,
  tableId,
}: ModalCreateColumnProps) => {
  const selectedTableFolder = useDataTableContext((s) => {
    const { tables } = s;
    return tables.find((table) => table.id === tableId)?.folder;
  });
  const createColumn = useDataTableContext((s) => s.createColumn);
  const [isLoading, setIsLoading] = useState(false);
  const [columnType, setColumnType] = useState<CreatableDataTableColumnType>();
  const [formId] = useState(htmlIdGenerator()());
  const [fields, setFields] = useState(INITIAL_FIELDS);

  const handleFieldChange = useCallback(
    <T extends keyof ModalCreateColumnFields>(
      fieldName: T,
      field: ModalCreateColumnFields[T],
    ) => {
      setFields((prevFields) => ({
        ...prevFields,
        [fieldName]: field,
      }));
    },
    [],
  );

  const handleSubmit = async () => {
    try {
      setIsLoading(true);
      const columnDef = fields.definition.value;
      assert(columnDef !== undefined);
      assert(
        columnDef.kind !== "resource_tier",
        `Column kind "${columnDef.kind}" cannot be created`,
      );
      const { error } = await createColumn({
        tableId,
        name: fields.name.value,
        defaultFormula: fields.default_formula.value,
        definition: columnDef,
        editable: COLUMN_KIND_MAP[columnDef.kind].editable,
      });
      assert(error === undefined);
      addUtilityToastSuccess("Column created");
    } catch (error) {
      addUtilityToastFailure("Failed to create column");
    } finally {
      setIsLoading(false);
      onClose();
    }
  };

  if (columnType === "join_existing_table") {
    return (
      <ModalCreateColumnFromExistingTable
        tableId={tableId}
        onBack={() => setColumnType(undefined)}
        onClose={onClose}
      />
    );
  }

  if (columnType === "new_analysis_table") {
    return (
      <ModalCreateAnalysisTable
        dataTableId={tableId}
        folderId={selectedTableFolder}
        onClose={onClose}
      />
    );
  }

  return (
    <EuiModal
      onClose={onClose}
      css={css`
        width: 600px;
      `}
    >
      <EuiModalHeader>
        <EuiModalHeaderTitle component="h3">
          Insert Column(s)
        </EuiModalHeaderTitle>
      </EuiModalHeader>

      <EuiModalBody>
        <EuiForm
          id={formId}
          component="form"
          role="form"
          onSubmit={(e) => {
            e.preventDefault();
            void handleSubmit();
          }}
        >
          <FieldColumnType
            autoFocus
            value={columnType}
            exclude={[
              "cell_status",
              "identifier",
              "join",
              "roi_frame",
              "resource_tier",
            ]}
            onChange={(columnType) => {
              setColumnType(columnType);
              if (columnType === undefined) {
                setFields(INITIAL_FIELDS);
              }
            }}
          />

          {(() => {
            switch (columnType) {
              case "boolean":
                return (
                  <PanelCreateColumnBoolean
                    fields={fields}
                    onFieldChange={handleFieldChange}
                  />
                );
              case "choice":
                return (
                  <PanelCreateColumnChoice
                    fields={fields}
                    onFieldChange={handleFieldChange}
                  />
                );
              case "date":
                return (
                  <PanelCreateColumnDate
                    fields={fields}
                    onFieldChange={handleFieldChange}
                  />
                );
              case "file":
                return (
                  <PanelCreateColumnFile
                    fields={fields}
                    onFieldChange={handleFieldChange}
                    singleSelection
                  />
                );
              case "metadatum":
                return (
                  <PanelCreateColumnFileMetadatum
                    tableId={tableId}
                    fields={fields}
                    onFieldChange={handleFieldChange}
                  />
                );
              case "float":
                return (
                  <PanelCreateColumnFloat
                    fields={fields}
                    onFieldChange={handleFieldChange}
                  />
                );
              case "integer":
                return (
                  <PanelCreateColumnInteger
                    fields={fields}
                    onFieldChange={handleFieldChange}
                  />
                );
              case "text":
                return (
                  <PanelCreateColumnText
                    fields={fields}
                    onFieldChange={handleFieldChange}
                  />
                );
              case "cell_status":
              case "identifier":
              case "join":
              case "roi_frame":
              case undefined:
                return null;
            }
          })()}
        </EuiForm>
      </EuiModalBody>

      <EuiModalFooter>
        <EuiButtonEmpty onClick={onClose}>Cancel</EuiButtonEmpty>
        <EuiButton
          type="submit"
          form={formId}
          isLoading={isLoading}
          fill
          disabled={Object.values(fields).some((field) => !field.isValid)}
        >
          Insert
        </EuiButton>
      </EuiModalFooter>
    </EuiModal>
  );
};
